import { createContext, ReactNode, useState, useMemo, useContext, Dispatch, SetStateAction, useEffect } from "react";
import { ColumnConfig, ColumnNameByRoute, FilterField, Identifiable, RouteEnum } from "layout/TableConstructor/types";

interface TableConstructorContextProps<
  T extends Identifiable,
  AvailableFilters extends object,
  OrderField extends string,
  CollapsingRowName extends string | undefined = undefined,
  Route extends RouteEnum | undefined = undefined,
> {
  setVisibleFilterList: Dispatch<SetStateAction<(keyof AvailableFilters | string)[]>>;
  visibleFilterList: (keyof AvailableFilters | string)[];
  visibleFilterConfig: FilterField<AvailableFilters>[];
  visibleColumnList: (ColumnNameByRoute<Route> | "action")[];
  setVisibleColumnList: Dispatch<SetStateAction<(ColumnNameByRoute<Route> | "action")[]>>;
  filterConfigList: FilterField<AvailableFilters>[];
  columnConfigList: ColumnConfig<T, AvailableFilters, OrderField, CollapsingRowName>[];
  visibleColumnConfig: ColumnConfig<T, AvailableFilters, OrderField, CollapsingRowName>[];
}

const TableConstructorContext = createContext<TableConstructorContextProps<any, any, any, any, any> | undefined>(
  undefined
);

interface TableConstructorProviderProps<
  T extends Identifiable,
  AvailableFilters extends object,
  OrderField extends string,
  CollapsingRowName extends string | undefined = undefined,
  Route extends RouteEnum | undefined = undefined,
> {
  children: ReactNode;
  defaultVisibleColumnList?: (ColumnNameByRoute<Route> | "action")[];
  columnConfigList: ColumnConfig<T, AvailableFilters, OrderField, CollapsingRowName, Route>[];
  filterConfigList: FilterField<AvailableFilters>[];
  storageName: string;
  dataLoading: boolean;
}

const TableConstructorProvider = <
  T extends Identifiable,
  AvailableFilters extends object,
  OrderField extends string,
  CollapsingRowName extends string | undefined = undefined,
  Route extends RouteEnum | undefined = undefined,
>({
  children,
  defaultVisibleColumnList,
  columnConfigList,
  filterConfigList,
  storageName,
  dataLoading,
}: TableConstructorProviderProps<T, AvailableFilters, OrderField, CollapsingRowName, Route>) => {
  const allColumns = columnConfigList.map((filter) => filter.name);

  const savedConfigString = localStorage.getItem(storageName);
  const savedColumnConfig: (ColumnNameByRoute<Route> | "action")[] | null = savedConfigString
    ? JSON.parse(savedConfigString)
    : null;

  const [visibleColumnList, setVisibleColumnList] = useState<(ColumnNameByRoute<Route> | "action")[]>(
    savedColumnConfig || defaultVisibleColumnList || allColumns
  );

  const allFilters = useMemo(
    () =>
      visibleColumnList.reduce((acc: (keyof AvailableFilters | string)[], columnName) => {
        const filterName = columnConfigList.find((c) => c.name === columnName)?.filterName;
        if (filterName) acc.push(filterName);
        return acc;
      }, []),
    [visibleColumnList]
  );

  const [visibleFilterList, setVisibleFilterList] = useState<(keyof AvailableFilters | string)[]>(allFilters);

  useEffect(() => {
    if (!dataLoading && !savedColumnConfig) {
      if (defaultVisibleColumnList) {
        setVisibleColumnList(defaultVisibleColumnList);
      } else {
        setVisibleColumnList(allColumns);
      }
    }
  }, [defaultVisibleColumnList, allColumns, dataLoading]);

  useEffect(() => {
    if (!dataLoading) {
      setVisibleFilterList(allFilters);
    }
  }, [dataLoading, allFilters]);

  const visibleFilterConfig = useMemo(
    () =>
      filterConfigList.reduce<FilterField<AvailableFilters>[]>((acc, filterConfig) => {
        const isFilterVisible = visibleFilterList.includes(filterConfig.name) || filterConfig.isHidden;
        if (isFilterVisible) acc.push(filterConfig);
        return acc;
      }, []),
    [visibleFilterList]
  );

  const visibleColumnConfig = useMemo(
    () =>
      columnConfigList.reduce<ColumnConfig<T, AvailableFilters, OrderField, CollapsingRowName, Route>[]>(
        (acc, columnConfig) => {
          const isColumnVisible = visibleColumnList.includes(columnConfig.name);
          if (isColumnVisible) acc.push(columnConfig);
          return acc;
        },
        []
      ),
    [visibleColumnList, columnConfigList]
  );

  const value: TableConstructorContextProps<T, AvailableFilters, OrderField, CollapsingRowName, Route> = useMemo(
    () => ({
      visibleFilterList,
      visibleColumnList,
      setVisibleFilterList,
      setVisibleColumnList,
      visibleFilterConfig,
      filterConfigList,
      columnConfigList,
      visibleColumnConfig,
    }),
    [
      visibleFilterList,
      visibleColumnList,
      setVisibleFilterList,
      setVisibleColumnList,
      visibleFilterConfig,
      filterConfigList,
      columnConfigList,
      visibleColumnConfig,
    ]
  );
  return <TableConstructorContext.Provider value={value}>{children}</TableConstructorContext.Provider>;
};

const useTableConstructor = <
  T extends Identifiable,
  AvailableFilters extends object,
  OrderField extends string,
  CollapsingRowName extends string | undefined = undefined,
  Route extends RouteEnum | undefined = undefined,
>(): TableConstructorContextProps<T, AvailableFilters, OrderField, CollapsingRowName, Route> => {
  const context = useContext(TableConstructorContext);
  if (context === undefined) {
    throw new Error("useTableConstructor must be used within a TableConstructorProvider");
  }
  return context;
};

export { TableConstructorProvider, useTableConstructor };
